import React from 'react';

import { Button, Flex, Table } from 'antd';
import { useTranslation } from 'react-i18next';

import i18n from 'segments/i18n';
import { defaultPaginationConfig } from 'segments/utils/constants/common';

import {
  convertProductsToTable,
  ProductInfo,
  useCreateProductMutation,
  useDeleteProductMutation
} from 'features/products';

export interface TableData extends ProductInfo {
  key: number;
}

type Props = {
  items: ProductInfo[];
  itemsTotal?: number;
  isLoading?: boolean;
  currentPage?: number;
  defaultPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  onEditClick: (id: number | string) => void;
};

export const ProductsTable = (props: Props) => {
  const {
    items,
    itemsTotal = 0,
    isLoading,
    currentPage = 1,
    defaultPageSize = 30,
    onPageChange,
    onEditClick,
  } = props;

  const [createProduct, { isLoading: isProductCreating }] = useCreateProductMutation();
  const [deleteProduct, { isLoading: isProductDeleting }] = useDeleteProductMutation();
  const { t } = useTranslation(undefined, { keyPrefix: 'table' });

  return (
    <Table<TableData>
      size="small"
      dataSource={convertProductsToTable(items)}
      loading={isLoading}
      scroll={{ x: 1200 }}
      pagination={{
        ...defaultPaginationConfig,
        defaultPageSize,
        current: currentPage,
        total: itemsTotal,
        onChange: onPageChange,
      }}
    >
      <Table.Column<TableData>
        title={t('name')}
        dataIndex="name"
        width={120}
        render={(name: string) => i18n.t(`products.items.${ name}`)}
      />
      <Table.Column<TableData>
        title={t('price')}
        dataIndex="price"
        width={120}
        render={(price: number) => `${Number(price).toFixed(0)}₽`}
      />
      <Table.Column<TableData>
        dataIndex="isOnline"
        render={(isOnline: boolean, { id, name, price }) => (
          <Flex justify='flex-end'>
            {
              isOnline && typeof id === 'number'
                ? (
                  <Button
                    type="primary" disabled={isProductDeleting} onClick={() => {
                      deleteProduct(id);
                    }}
                    danger
                  >
                    {t('disactive')}
                  </Button>
                )
                : (
                  <Button
                    type="primary" onClick={() => {
                      createProduct({ name, price });
                    }}
                    disabled={isProductCreating}
                  >
                    {t('active')}
                  </Button>
                )
            }
          </Flex>
        )}
      />
      <Table.Column<TableData>
        dataIndex="id"
        width={60}
        render={(id: string | number) => (
          <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
            <Button type="primary" onClick={() => onEditClick(id)}>
              {t('edit', 'Редактировать')}
            </Button>
          </div>
        )}
      />
    </Table>
  );
};
